import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Save, Plus } from 'lucide-react';
import { apiErrorMessage } from '@/lib/apiError';
import { SelectMargen, SelectTarifaCobertura, notaMargen } from '@/components/NivelesDePrecio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARRIERS = ['USPS', 'UPS', 'FedEx', 'DHL'];

const VACIO = {
  name: '', description: '', coverage_limit: '', coverage_rate_cents: '', webship_markup_pct: '', shipping_markup_pct: '',
  international_allowed: true, local_delivery_allowed: true, photo_fee_waived: false, carriers: CARRIERS, note: '',
};

const borrador = (p) => ({
  name: p.name, description: p.description || '',
  coverage_limit: p.settings.coverage_limit ?? '',
  coverage_rate_cents: p.settings.coverage_rate_cents ?? '',
  webship_markup_pct: p.settings.webship_markup_pct ?? '',
  shipping_markup_pct: p.settings.shipping_markup_pct ?? '',
  international_allowed: p.settings.international_allowed,
  local_delivery_allowed: p.settings.local_delivery_allowed,
  photo_fee_waived: p.settings.photo_fee_waived === true,
  carriers: p.settings.carriers_allowed ?? CARRIERS,
  note: '',
});

const numero = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const ajustes = (d) => ({
  coverage_limit: numero(d.coverage_limit),
  coverage_rate_cents: numero(d.coverage_rate_cents),
  webship_markup_pct: numero(d.webship_markup_pct),
  shipping_markup_pct: numero(d.shipping_markup_pct),
  international_allowed: d.international_allowed,
  local_delivery_allowed: d.local_delivery_allowed,
  photo_fee_waived: d.photo_fee_waived,
  carriers_allowed: d.carriers.length === CARRIERS.length ? null : d.carriers,
});

const resumen = (s) => [
  s.coverage_limit ? `limit $${Number(s.coverage_limit).toLocaleString()}` : 'category limit',
  s.coverage_rate_cents ? `${s.coverage_rate_cents}¢/100` : 'standard rate',
  s.shipping_markup_pct ? `margin ${s.shipping_markup_pct}%` : 'default margin',
  s.webship_markup_pct ? `WebShip +${s.webship_markup_pct}%` : 'WebShip at cost',
  s.international_allowed ? 'international' : 'domestic only',
  s.local_delivery_allowed ? 'local delivery' : 'no local delivery',
  s.photo_fee_waived ? 'photo free' : 'photo 1.99',
  s.carriers_allowed ? s.carriers_allowed.join('/') : 'all carriers',
].join(' · ');

/**
 * Los planes armados (Mike, 10-oct): plantillas del setup que se asignan a un
 * comerciante y despues se ajustan. Editar un plan no cambia a quien ya lo
 * tiene. No tienen precio: el cobro mensual no existe todavia.
 */
export default function MerchantPlansPanel({ token, plans, onChanged, niveles }) {
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [editando, setEditando] = useState(null); // 'new' | plan id
  const [d, setD] = useState(VACIO);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setD((x) => ({ ...x, ...patch }));

  const abrir = (p) => { setEditando(p ? p.id : 'new'); setD(p ? borrador(p) : VACIO); };

  const guardar = async () => {
    setBusy(true);
    try {
      const nuevo = editando === 'new';
      const body = nuevo
        ? { name: d.name, description: d.description, settings: ajustes(d) }
        : { name: d.name, description: d.description, settings: ajustes(d), note: d.note };
      const r = await fetch(`${API}/merchants/plans${nuevo ? '' : `/${editando}`}`, { method: 'POST', headers: auth, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(apiErrorMessage(j, 'Plan not saved.'));
      toast.success(nuevo ? `Plan ${d.name} created` : `Plan ${d.name} saved`);
      setEditando(null);
      await onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const archivar = async (p, activo) => {
    const note = window.prompt(`Why ${activo ? 'restore' : 'archive'} ${p.name}? (logged)`);
    if (!note || !note.trim()) return;
    const r = await fetch(`${API}/merchants/plans/${p.id}`, { method: 'POST', headers: auth, body: JSON.stringify({ active: activo, note }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(apiErrorMessage(j, 'Not saved.')); return; }
    await onChanged();
  };

  return (
    <Card data-testid="merchant-plans">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          Plans
          <Button size="sm" variant="outline" onClick={() => abrir(null)} data-testid="plan-new"><Plus className="w-4 h-4 mr-1" /> New plan</Button>
        </CardTitle>
        <CardDescription>
          Ready-made setups to assign to a merchant and adjust afterwards. Editing a plan does not change merchants
          who already have it: assign it again to pass the change on. Plans carry no price: monthly billing is not built,
          so nothing here is charged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {plans.length === 0 && editando === null && <p className="text-sm text-slate-500">No plans yet.</p>}
        {plans.map((p) => (
          <div key={p.id} className={`flex items-start justify-between gap-3 border rounded-lg p-3 ${p.active ? '' : 'opacity-60'}`} data-testid={`plan-${p.name}`}>
            <div className="min-w-0">
              <p className="font-medium text-sm">{p.name} {!p.active && <span className="text-xs text-slate-500">(archived)</span>}</p>
              {p.description && <p className="text-xs text-slate-500">{p.description}</p>}
              <p className="text-xs text-slate-500 mt-1">{resumen(p.settings)} · {p.merchants} merchant(s)</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => abrir(p)} data-testid={`plan-edit-${p.name}`}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => archivar(p, !p.active)}>{p.active ? 'Archive' : 'Restore'}</Button>
            </div>
          </div>
        ))}

        {editando !== null && (
          <div className="border rounded-lg p-3 space-y-3" data-testid="plan-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={d.name} onChange={(e) => set({ name: e.target.value })} data-testid="plan-name" /></div>
              <div><Label>Description</Label><Input value={d.description} onChange={(e) => set({ description: e.target.value })} /></div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div><Label>Coverage limit without a code (USD)</Label>
                <Input type="number" min="1" max="25000" value={d.coverage_limit} onChange={(e) => set({ coverage_limit: e.target.value })} placeholder="Category default" data-testid="plan-limit" /></div>
              <div><Label>Coverage rate (¢ per $100)</Label>
                <SelectTarifaCobertura niveles={niveles} value={d.coverage_rate_cents} onChange={(v) => set({ coverage_rate_cents: v })} testid="plan-rate" /></div>
              <div><Label>Shipping margin</Label>
                <SelectMargen niveles={niveles} value={d.shipping_markup_pct} onChange={(v) => set({ shipping_markup_pct: v })} testid="plan-margin" />
                <p className="text-xs text-slate-500 mt-1">{notaMargen(niveles)}</p></div>
              <div><Label>WebShip markup (%)</Label>
                <Input type="number" min="0" max="50" step="0.5" value={d.webship_markup_pct} onChange={(e) => set({ webship_markup_pct: e.target.value })} placeholder="Default: 0" /></div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={d.international_allowed} onChange={(e) => set({ international_allowed: e.target.checked })} /> International</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={d.local_delivery_allowed} onChange={(e) => set({ local_delivery_allowed: e.target.checked })} /> Local delivery</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={d.photo_fee_waived} onChange={(e) => set({ photo_fee_waived: e.target.checked })} /> Item photo free</label>
              {CARRIERS.map((c) => (
                <label key={c} className="flex items-center gap-2">
                  <input type="checkbox" checked={d.carriers.includes(c)}
                    onChange={(e) => set({ carriers: e.target.checked ? CARRIERS.filter((x) => x === c || d.carriers.includes(x)) : d.carriers.filter((x) => x !== c) })} />
                  {c}
                </label>
              ))}
            </div>
            {editando !== 'new' && (
              <div><Label>Why (required, it is logged)</Label><Input value={d.note} onChange={(e) => set({ note: e.target.value })} data-testid="plan-note" /></div>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={guardar} disabled={busy || !d.name.trim() || (editando !== 'new' && !d.note.trim())} data-testid="plan-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save plan
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditando(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
